import { ApiError } from './errors';
import type { Page, Transition } from './types';

/**
 * Boundary validation. ⭐ The point is not defensive politeness — it is that a shape mismatch must
 * become a VISIBLE error rather than a page of blank fields. A row rendering `undefined-undefined`
 * for its key looks like missing data; an error naming the endpoint looks like a bug, which it is.
 */

function fail(url: string, what: string): never {
  throw new ApiError({ kind: 'bad_response', url, reason: what });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The API wraps every collection as `{ items: [...] }`; bare arrays and `{data}` are also accepted. */
export function asArray<T>(body: unknown, url: string): T[] {
  if (Array.isArray(body)) return body as T[];
  if (isRecord(body)) {
    if (Array.isArray(body.items)) return body.items as T[];
    if (Array.isArray(body.data)) return body.data as T[];
  }
  return fail(url, 'expected a list');
}

/** Pulls a named key out of an envelope, e.g. `{ ticket: {...} }` → the ticket. */
export function unwrap<T>(body: unknown, key: string, url: string): T {
  if (!isRecord(body)) return fail(url, `expected an object carrying "${key}"`);
  const value = body[key];
  if (value === undefined || value === null) return fail(url, `response carried no "${key}"`);
  return value as T;
}

function readNumber(source: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

/**
 * `GET /api/v1/tickets` → `{ items, total, limit, offset }`.
 *
 * ⭐ The API paginates by OFFSET; the UI reads by PAGE. Converting in exactly one place means no
 * component ever holds both and gets the off-by-one.
 *
 * ⚠️ `total` is load-bearing (Or's real project holds 486 items) and is NOT guessed. If the API sent
 * a bare array we refuse rather than setting `total = items.length`, which would print "50 of 50" on
 * page one of 486 — a confident wrong number nobody would question.
 */
export function asPage<T>(
  body: unknown,
  url: string,
  requested: { page: number; per_page: number },
): Page<T> {
  if (Array.isArray(body)) {
    return fail(url, 'expected a paginated response with a total, received a bare array');
  }
  if (!isRecord(body)) return fail(url, 'expected a paginated response');

  const meta = isRecord(body.meta) ? body.meta : body;
  const items = asArray<T>(body, url);
  const total = readNumber(meta, ['total', 'total_count', 'count']);
  if (total === null) return fail(url, 'paginated response carried no total');

  const perPage = readNumber(meta, ['limit', 'per_page', 'page_size']) ?? requested.per_page;
  const offset = readNumber(meta, ['offset']);
  const page =
    offset !== null && perPage > 0
      ? Math.floor(offset / perPage) + 1
      : (readNumber(meta, ['page']) ?? requested.page);

  return { items, total, page, per_page: perPage };
}

/**
 * `GET /tickets/:id/transitions` →
 *   `{ ticket_id, current_status, suggested: [...], allowed: [...], transitions_are_advisory: true }`
 *
 * 🔴🔴 `suggested` IS THE FIELD TO READ. `allowed` IS A DEPRECATED ALIAS FOR THE SAME ARRAY.
 *   The API renamed it on 2026-08-17 (PH-19) precisely because the old name was being read as a
 *   PERMISSION LIST, which it never was and certainly is not now: any ACTIVE status is a legal
 *   target. The alias is kept only so the deployed board and the MCP server do not break on the day
 *   it ships. ⛔ Reading `allowed` FIRST would keep taking the compatibility shim as the truth.
 *
 * ⚠️ The fallback order matters and is `suggested → allowed → bare array`. An older deployment sends
 * only `allowed`; a newer one sends both, identical. Preferring `suggested` therefore changes
 * nothing today and stops mattering the wrong way round the moment the alias is dropped.
 *
 * ⛔ What this never does is substitute a default when the call FAILS. An empty list means "nothing
 * is suggested from here" — a real answer, and NOT "you may not move" — and a failure means "we do
 * not know". The dropdown and the board show those differently.
 */
export function asTransitions(body: unknown, url: string): Transition[] {
  const raw = !isRecord(body)
    ? asArray<unknown>(body, url)
    : Array.isArray(body.suggested)
      ? body.suggested
      : Array.isArray(body.allowed)
        ? body.allowed
        : asArray<unknown>(body, url);

  return (raw as unknown[]).flatMap((entry): Transition[] => {
    if (typeof entry === 'string') return [{ to_status: entry, label: null, colour: null, note: null }];
    if (!isRecord(entry)) return [];
    // The API calls the target `code`; older drafts called it `to_status`. Accept both, once, here.
    const target = entry.code ?? entry.to_status ?? entry.to ?? entry.status;
    if (typeof target !== 'string') return [];
    return [
      {
        to_status: target,
        label: typeof entry.label === 'string' ? entry.label : null,
        colour: typeof entry.colour === 'string' ? entry.colour : null,
        note: typeof entry.note === 'string' ? entry.note : null,
      },
    ];
  });
}

/**
 * 🔴🔴 THE SEMANTICS OF THE SERVER WE ARE ACTUALLY TALKING TO — `transitions_are_advisory`.
 *
 * The API added this flag so a client can DETECT which rules are in force rather than assume them
 * (`routes/tickets.ts`: *"an explicit statement of the rule, so a client can DETECT which semantics
 * the server it is talking to has"*).
 *
 * ⛔ STRICT `=== true`, AND THE DEFAULT IS `false` — the CAUTIOUS direction. An older deployment
 * that still enforces the transition graph sends no such key, and treating its absence as "advisory"
 * would let this board offer drops that server refuses, which is the pre-PH-19 failure with the
 * sign flipped. ⭐ A missing flag is not a promise.
 */
export function readsAdvisory(body: unknown): boolean {
  return isRecord(body) && body.transitions_are_advisory === true;
}
